import { ListItem } from "~/models/list";
import { ImageData } from "~/models/image";
import { wpFetch } from "./wpfetch";
import { API_WP_PAGES_URL } from "./base";
import { getRelativeUrl } from "~/utils/url";
import { CardItemLabel } from "~/widgets/card/components/CardItem";
import { Logger } from "~/utils/logger";

const logger = new Logger("page-api", "info");

export type ListPageItem = {
  id: number;
  title: string;
  description: string;
  link: string;
  imageId: number | null;
  image: Record<string, ImageData>;
  focalPoint: { x: number; y: number };
};

export type CardListItem = {
  id: number;
  title: string;
  description: string;
  link: string;
  image: ImageData;
  focalPoint: { x: number; y: number };
  searchTags: string[];
  label?: CardItemLabel;
};

export const getPageById = async (
  listId: number | string,
): Promise<ListPageItem> => {
  const syncList = await wpFetch(`${API_WP_PAGES_URL}/${listId}`);
  return syncList;
};

type CardProps = {
  internalPage?: string;
  list?: ListItem;
  externalLink?: {
    url: string;
  };
  externalImage?: {
    url: string;
  };
  externalTitle?: string;
  externalDescription?: string;
  externalFocalPoint?: { x: number; y: number };
  searchTags?: string[];
  pillShow?: string;
  pillText?: string;
  pillColor?: string;
  pillBgColor?: string;
};

const mergeListAndPage = (
  list: ListItem | undefined,
  searchTags: string[] | undefined,
  pillData: CardItemLabel | undefined,
  page: ListPageItem | undefined,
): CardListItem => {
  const listSync = list?.listSync === "yes";
  const title = listSync && page ? page.title : list?.listTitle ?? "";
  const link = page ? page.link : "";
  const description =
    listSync && page ? page.description : list?.listDescription ?? "";
  const image =
    listSync && page ? page.image["large"] : { url: list?.listImageUrl ?? "" };
  const focalPoint = list?.listImageFocalPoint
    ? {
        x: list.listImageFocalPoint.x,
        y: list.listImageFocalPoint.y,
      }
    : { x: 0.5, y: 0.5 };

  return {
    id: parseInt(list?.listId.toString() ?? "-1", 10),
    title: title,
    description: description,
    link: getRelativeUrl(link ?? ""), // make local page links relative
    image: image,
    focalPoint: focalPoint,
    searchTags: searchTags ?? [],
    label: pillData,
  };
};

const isInternalPage = (card: CardProps): boolean => {
  return card.internalPage === undefined || card.internalPage === "yes";
};

export const getCards = async (
  cards?: CardProps[],
): Promise<CardListItem[]> => {
  if (!cards) {
    logger.warn("No cards provided to getCards");
    return [];
  }

  const ids = cards
    .filter((card) => {
      return isInternalPage(card);
    })
    .map((card) => card.list?.listId)
    .filter((id): id is number => id !== undefined);

  logger.info("fetching card details for list ids:", ids.join(","));
  const pages = await wpFetch(`${API_WP_PAGES_URL}?ids=${ids.join(",")}`);

  // duplicate list ids in the query will be ignored by the REST endpoint
  // so we need to take the result and merge it back with the original list of
  // cards to make sure we return the correct number of card details
  // (including duplicates if there are duplicate list ids in the input)
  return cards.map((card) => {
    if (!isInternalPage(card)) {
      // logger.debug("focal point for external card", card.externalFocalPoint);

      // just return the external card details as-is without trying to merge with a page
      return {
        id: -1, // no page id for external cards
        title: card.externalTitle || "",
        description: card.externalDescription || "",
        link: card.externalLink?.url || "",
        image: card.externalImage
          ? { url: card.externalImage.url }
          : { url: "" },
        focalPoint: card.externalFocalPoint
          ? card.externalFocalPoint
          : { x: 0.5, y: 0.5 }, // default focal point for external images
        searchTags: card.searchTags || [],
        pillData:
          card.pillShow === "yes"
            ? {
                text: card.pillText ?? "",
                color: card.pillColor ?? "#000000",
                backgroundColor: card.pillBgColor ?? "#ffffff",
              }
            : undefined,
      };
    }

    const listId = card.list?.listId ?? -1;

    const page = pages.find(
      (page: any) =>
        parseInt(page.id.toString(), 10) === parseInt(listId.toString(), 10),
    );
    if (!page) {
      logger.error(`Page not found for id ${listId}`);
      logger.error("Available pages:", pages);
    }

    return mergeListAndPage(
      card.list,
      card.searchTags,
      card.pillShow === "yes"
        ? {
            text: card.pillText ?? "",
            color: card.pillColor ?? "#000000",
            backgroundColor: card.pillBgColor ?? "#ffffff",
          }
        : undefined,
      page,
    );
  });
};

export const getPages = async (): Promise<ListPageItem[]> => {
  const pages = await wpFetch(API_WP_PAGES_URL);
  return pages;
};
